import type { WriteBackErrorCode, WriteBackResult } from "@ext/elation/elationWriteBack";

/**
 * Turn a write-back result into copy a clinician can act on.
 *
 * Raw codes (EL8_UNAVAILABLE, OPEN_NOTE_MISMATCH, …) are for the console, never
 * the screen — they tell a clinician nothing and read as a broken product. Every
 * message here names either what to do or what went wrong in chart terms.
 */
const MESSAGES: Partial<Record<WriteBackErrorCode, string>> = {
  // Wording deliberately contains the phrase "open note" — that is the term the
  // rest of the Encounter tab uses for the note being edited, and the test
  // asserts on it.
  NO_OPEN_NOTE: "There's no open note in Elation. Open one, then try again.",
  SIGNED_NOTE_UNSUPPORTED: "This note is already signed and can't be edited.",
  PATIENT_MISMATCH:
    "Elation is showing a different patient. Switch back and try again.",
  PATIENT_TAB_NOT_FOUND:
    "Couldn't find the Elation tab. Make sure the chart is open.",
  EL8_UNAVAILABLE:
    "Couldn't reach the open note. Refresh the Elation tab and try again.",
  OPEN_NOTE_MISMATCH: "The open note changed. Refresh and try again.",
  SECTION_NOT_WRITABLE: "That section doesn't accept typed text.",
  SECTION_UNRESOLVED: "Couldn't find that section in the open note.",
  EMPTY_TEXT: "There's nothing to write.",
  ENGINE_UNSUPPORTED: "Writing isn't supported for this note template yet.",
  // The POST returned 2xx and the re-GET did not find the text, so the write MAY
  // have landed. Both halves of the generic advice — "try again" and "copy the
  // text in manually" — put a duplicate bullet in a note that can then be signed
  // off, so this must NOT invite a retry; it must send the clinician to look.
  READ_AFTER_WRITE_FAILED:
    "The write may not have saved — check the note before trying again.",
  // Same shape as READ_AFTER_WRITE_FAILED and for the same reason: the text was
  // assigned to a checklist field and could not be read back out of the live
  // note, so part of it may have landed. Retrying could duplicate it, so send
  // the clinician to look rather than inviting another press.
  WRITE_UNVERIFIED:
    "Couldn't confirm what was written — check the exam in Elation before trying again.",
  // Session/cookie dead end: retrying fails identically forever, so name the one
  // thing that actually clears it.
  CSRF_MISSING:
    "Your Elation session expired. Refresh the Elation tab and try again.",
};

const GENERIC =
  "Couldn't write to the open note. Try again, or copy the text in manually.";

export function writeBackMessage(result: WriteBackResult): string {
  if (result.ok) return "";
  return (result.errorCode && MESSAGES[result.errorCode]) || GENERIC;
}
